<!-- ══ DROPZONE ════════════════════════════════════════════════════════════
     One `--surface-2` field with a dashed border, and it is a TRIGGER ONLY: the
     upload glyph, the title and the hint, in every state.

     It used to swap those for the picked chips once a file was chosen, which is
     what made a just-picked file invisible: the note's attachments are listed
     BELOW the field — that is where the stored ones have always been, and where
     the count line counts them — so a pick that rendered inside the field landed
     in the one place the reader was not looking, and vanished from the list they
     were watching. The chips are the host's, all of them, in one row.

     Keeping the prompt at every count is the other half: the field is how the
     SECOND file gets added, and a field that stops saying so once it holds
     something is a field that looks finished. This is the arrangement the shared
     Add/Edit Note dialog has always used. -->
@if (mode === 'dropzone') {
  <div class="na-pick">
    <!-- The field is ALWAYS a live click/drop target. There used to be a second,
         disabled state for "you have hit the 10-file cap"; there is no cap now,
         so a note can always take one more file and that state is unreachable. -->
    <label class="drop" [class.drop-compact]="compact" [attr.for]="inputId"
           [title]="dropHint"
           [attr.aria-label]="'Attach files. ' + dropHint"
           appUploadFile [multiple]="true" (files)="onDropped($event)">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
           stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <path d="M17 8l-5-5-5 5M12 3v12"/>
      </svg>
      @if (compact) {
        <!-- One sentence, as the reference writes it: the prompt and the rule
             on the same line, separated by its em dash. Still plural, because
             this field takes as many files as the stacked one does — the
             reference's own copy is singular only because its input is a mock
             that hard-codes one filename. -->
        <span class="drop-line">Select files or drag them here - {{ dropLineHint }}</span>
      } @else {
        <span class="drop-title">Select files or drag them here</span>
        <span class="drop-hint">{{ dropHint }}</span>
      }
    </label>

    <input [id]="inputId" type="file" hidden multiple [accept]="accept"
           [disabled]="disabled" (change)="onInputChange($event)"/>
  </div>
}

<!-- ══ BUTTON — the inline reply composer ══════════════════════════════════
     The attachment DESIGN is the same pill; only the trigger differs, because
     this composer is a narrow editor with a vertical action column and has no
     room for a dropzone field. The paperclip is the control that already sits
     there, next to the send button.

     This mode renders the TRIGGER ONLY, and deliberately does not render a file
     list. It is used exclusively by `internal-notes`, which is on the OLD design:
     its picked files render below the validation message as the legacy
     `.internal-notes-files` list, drawn by the host in its own stylesheet. Giving
     this mode a list would put the new-design chip on a legacy page — so the
     invariant is structural here rather than a flag each caller must remember. -->
@if (mode === 'button') {
  <div class="file-upload-actions">
    <!-- The legacy composer's own button, markup and asset unchanged from what
         `internal-notes.component.html` held inline — only `multiple` on the
         input is new. Nothing about this trigger is redesigned: it is on a page
         still running the old console CSS. -->
    <!-- The label is dismissed AND suppressed for the life of the OS file dialog —
         `openPicker()` does both, see the comment on `tipSuppressed`.

         The last version of this only called `uploadTip.hide()` from here, on the
         claim that nothing could re-show it. That claim was wrong: the paperclip
         keeps DOM focus while the OS dialog owns the window, so closing the dialog
         has Chrome re-dispatch `focus` to the button, and `focus` is half of
         ngx-bootstrap's default `triggers: 'hover focus'`. The label came back and
         then had nothing to close it — the pointer is over where the dialog's Open
         button was, so no `mouseout` ever follows.

         `[isDisabled]` is what actually holds it shut (`show()` returns early on
         it), and only a real pointer crossing of this button lifts it — the one
         signal the file dialog cannot fake. -->
    <button type="button" class="btn-file-upload" [disabled]="disabled"
            (click)="openPicker()"
            (mouseenter)="releaseTip()" (mouseleave)="releaseTip()"
            [tooltip]="'Upload File'" #uploadTip="bs-tooltip"
            [isDisabled]="tipSuppressed"
            aria-label="Attach files to this reply">
      <img src="/assets/admin/images/upload-icon.svg" width="22" height="22" alt="attach-file">
    </button>
    <input #fileInput type="file" style="display: none;" multiple [accept]="accept"
           [disabled]="disabled" (change)="onInputChange($event)">
  </div>
}
